import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "@/components/ui/DropDown";
import Button from "@/components/ui/Button";
import useCampaignDetailStore from "../store/useCampaignDetailStore";
import useCampaignStore from "@/pages/campaign/store/campaignStore";
import useGlobalStore from "@/store/globalStore";
import { isAlreadyRestored } from "@/api/restore";
import DeleteConfirmationControl from "./delete-confirmation-control";
// `FileText` was the draft-status icon, which the coloured status circles
// replaced, so it is no longer imported.
import { ChevronDown, ChevronUp, Download } from "lucide-react";
import Dashboard_Card from "@/assets/Dashboard_Card.png";

/**
 * Status colours, lifted from the fill values of the status icons these
 * circles replaced (assets/home/card/Icon*.svg), so the colour-to-status
 * mapping users already know is unchanged. Draft had no icon and keeps the
 * grey it was drawn in.
 */
const STATUS_COLORS = {
  draft: "#d1d5db",
  briefed: "#ffe348",
  in_progress: "#ffab48",
  in_review: "#eb6e6e",
  approved: "#d4e507",
  trafficking: "#84a0ff",
  live: "#4ed5e6",
} as const;

interface FilterState {
  status: string;
  language: string;
  targeting: string;
  category: string;
  publisher: string;
}

interface AssetRow {
  id: string;
  assetId: string;
  thumbnail: string;
  language: string;
  objective: string;
  region: string;
  category: string;
  publisher: string;
  adType: string;
  dueDate: string | null;
  statusCounts: Record<string, number>;
}

export default function CampaignDetailListView() {
  const navigate = useNavigate();
  const {
    campaignDetail,
    getCampaignDetailData,
    deleteFlight,
    deleteAsset,
    restoreFlight,
    restoreAsset,
    getDeletedItems,
  } = useCampaignDetailStore();
  const briefCampaignAssets = useCampaignStore((state) => state.briefCampaignAssets);
  const downloadCampaignAssets = useCampaignStore((state) => state.downloadCampaignAssets);
  const showSuccess = useGlobalStore((state) => state.showSuccess);
  const showInfo = useGlobalStore((state) => state.showInfo);
  const setError = useGlobalStore((state) => state.setError);

  const [expandedFlights, setExpandedFlights] = useState<Record<string, boolean>>({});
  const [isBriefing, setIsBriefing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    status: "All",
    language: "All",
    targeting: "All",
    category: "All",
    publisher: "All",
  });

  // Extract filter options from campaign data
  const getFilterOptions = () => {
    if (!campaignDetail) return {
      statusOptions: [{ title: "All", value: "All" }],
      languageOptions: [{ title: "All", value: "All" }],
      targetingOptions: [{ title: "All", value: "All" }],
      categoryOptions: [{ title: "All", value: "All" }],
      publisherOptions: [{ title: "All", value: "All" }],
    };

    const languages = new Set<string>();
    const regions = new Set<string>();
    const categories = new Set<string>();

    campaignDetail.nested_by_flight?.forEach(flight => {
      flight.flight_rows?.forEach(row => {
        if (row?.language) languages.add(row.language);
        if (row?.region) regions.add(row.region);
        row.flight_ad_categories?.forEach(cat => {
          if (cat?.ad_category_name) categories.add(cat.ad_category_name);
        });
      });
    });

    return {
      statusOptions: [
        { title: "All", value: "All" },
        { title: "Briefed", value: "briefed" },
        { title: "In Progress", value: "in_progress" },
        { title: "In Review", value: "in_review" },
        { title: "Approved", value: "approved" },
      ],
      languageOptions: [
        { title: "All", value: "All" },
        ...Array.from(languages).map(lang => ({ title: lang, value: lang }))
      ],
      targetingOptions: [
        { title: "All", value: "All" },
        ...Array.from(regions).map(region => ({ title: region, value: region }))
      ],
      categoryOptions: [
        { title: "All", value: "All" },
        ...Array.from(categories).map(cat => ({ title: cat, value: cat }))
      ],
      publisherOptions: [
        { title: "All", value: "All" },
        { title: "Meta", value: "Meta" },
        { title: "Pinterest", value: "Pinterest" },
        { title: "Snapchat", value: "Snapchat" },
      ],
    };
  };

  const filterOptions = getFilterOptions();

  // Auto-expand all flights on load
  useEffect(() => {
    if (campaignDetail?.nested_by_flight) {
      const expanded: Record<string, boolean> = {};
      campaignDetail.nested_by_flight.forEach((_, index) => {
        expanded[`flight-${index}`] = true;
      });
      setExpandedFlights(expanded);
    }
  }, [campaignDetail]);

  const toggleFlight = (flightKey: string) => {
    setExpandedFlights(prev => ({
      ...prev,
      [flightKey]: !prev[flightKey],
    }));
  };

  // Move every draft asset version in this campaign to "briefed" in one click.
  // Only draft versions are affected — the backend leaves assets in any other
  // status untouched.
  const handleBriefAssets = async () => {
    const campaignId = campaignDetail?.metadata.campaign.id;
    if (!campaignId || isBriefing) return;

    setIsBriefing(true);
    try {
      const result = await briefCampaignAssets(campaignId);
      if (result.transitioned > 0) {
        showSuccess(
          `${result.transitioned} draft asset${
            result.transitioned === 1 ? "" : "s"
          } moved to briefed.`,
        );
      } else {
        showInfo("No draft assets to move to briefed.");
      }
      // Refresh dashboard data so the updated status counts are reflected
      await getCampaignDetailData(campaignId);
    } catch (error: any) {
      setError(error.message ?? "Failed to move assets to briefed");
    } finally {
      setIsBriefing(false);
    }
  };

  // Download the full-campaign asset export workbook (campaign → flights →
  // assets → versions → media, with public S3 links).
  const handleExportAssets = async () => {
    const campaignId = campaignDetail?.metadata.campaign.id;
    if (!campaignId || isExporting) return;

    setIsExporting(true);
    try {
      await downloadCampaignAssets(campaignId);
    } catch (error: any) {
      setError(error.message ?? "Failed to export campaign assets");
    } finally {
      setIsExporting(false);
    }
  };

  // Undo of a just-completed delete, run straight from the toast (no
  // confirmation — the point of an undo is that it is frictionless). A 404
  // NO_RESTORABLE_DELETION means it is already back, which is not a failure and
  // must not raise a red toast; a 409 carries a message naming the ancestor
  // that has to be restored first, and that message is shown as-is.
  const handleUndoDelete = async (
    kind: "flight" | "asset",
    id: string,
  ): Promise<void> => {
    const campaignId = campaignDetail?.metadata.campaign.id;
    // The "Recently deleted" panel on this page reads the same store slice, so
    // it is re-fetched alongside the campaign data either way.
    const refreshDeletedItems = () =>
      campaignId
        ? getDeletedItems(campaignId).catch(() => undefined)
        : Promise.resolve();
    try {
      if (kind === "flight") {
        await restoreFlight(id);
      } else {
        await restoreAsset(id);
      }
      if (campaignId) {
        await getCampaignDetailData(campaignId);
      }
      await refreshDeletedItems();
      showSuccess(kind === "flight" ? "Flight restored." : "Asset restored.");
    } catch (error: any) {
      if (isAlreadyRestored(error)) {
        if (campaignId) {
          await getCampaignDetailData(campaignId);
        }
        await refreshDeletedItems();
        showInfo(
          kind === "flight"
            ? "That flight has already been restored."
            : "That asset has already been restored.",
        );
        return;
      }
      setError(error?.message ?? `Failed to restore ${kind}`);
    }
  };

  // Delete a flight, then refresh the campaign data so the view reflects the
  // removal. Errors (including 423 lock messages) are surfaced by the caller.
  const handleDeleteFlight = async (flightId: string) => {
    const campaignId = campaignDetail?.metadata.campaign.id;
    await deleteFlight(flightId);
    if (campaignId) {
      await getCampaignDetailData(campaignId);
      await getDeletedItems(campaignId).catch(() => undefined);
    }
    showSuccess("Flight deleted.", undefined, {
      label: "Undo",
      onClick: () => handleUndoDelete("flight", flightId),
    });
  };

  // Delete an asset, then refresh the campaign data.
  const handleDeleteAsset = async (assetId: string) => {
    const campaignId = campaignDetail?.metadata.campaign.id;
    await deleteAsset(assetId);
    if (campaignId) {
      await getCampaignDetailData(campaignId);
      await getDeletedItems(campaignId).catch(() => undefined);
    }
    showSuccess("Asset deleted.", undefined, {
      label: "Undo",
      onClick: () => handleUndoDelete("asset", assetId),
    });
  };

  // Transform campaign data into asset rows
  const getFlightAssets = (flightId: string): AssetRow[] => {
    const flight = campaignDetail?.nested_by_flight.find(f => f.flight_id === flightId);
    if (!flight) return [];

    const assets: AssetRow[] = [];

    flight.flight_rows.forEach(row => {
      row.flight_ad_categories.forEach(category => {
        category.ad_rows.forEach((ad, index) => {
          assets.push({
            id: `${ad.asset_id}-${index}`,
            assetId: ad.asset_id,
            thumbnail: campaignDetail.metadata.header_image?.url || Dashboard_Card,
            language: row.language,
            objective: row.objective,
            region: row.region || "N/A",
            category: category.ad_category_name || "Social",
            publisher: ad.publisher_name || "user_defined",
            adType: ad.ad_type,
            // Per-asset due date from the DB (parameter_value_json.due_dates)
            dueDate: ad.due_date ?? null,
            statusCounts: {
              draft: ad.status_counts?.draft || 0,
              briefed: ad.status_counts?.briefed || 0,
              in_progress: ad.status_counts?.in_progress || 0,
              in_review: ad.status_counts?.in_review || 0,
              approved: ad.status_counts?.approved || 0,
              trafficking: ad.status_counts?.trafficking || 0,
              live: ad.status_counts?.live || 0,
            },
          });
        });
      });
    });

    return assets;
  };

  // Apply filters to assets
  const filterAssets = (assets: AssetRow[]): AssetRow[] => {
    return assets.filter(asset => {
      if (filters.language !== "All" && asset.language !== filters.language) return false;
      if (filters.targeting !== "All" && asset.region !== filters.targeting) return false;
      if (filters.category !== "All" && asset.category !== filters.category) return false;
      if (filters.publisher !== "All" && asset.publisher !== filters.publisher) return false;
      return true;
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };



  if (!campaignDetail) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading campaign data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <div className="flex items-center gap-4">
        <span className="font-medium text-gray-700">Filter by:</span>
        <Dropdown
          options={filterOptions.statusOptions}
          selected={filters.status}
          setSelected={(value) => setFilters(prev => ({ ...prev, status: typeof value === 'function' ? value(prev.status) : value }))}
          placeholder="Status"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.languageOptions}
          selected={filters.language}
          setSelected={(value) => setFilters(prev => ({ ...prev, language: typeof value === 'function' ? value(prev.language) : value }))}
          placeholder="Language"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.targetingOptions}
          selected={filters.targeting}
          setSelected={(value) => setFilters(prev => ({ ...prev, targeting: typeof value === 'function' ? value(prev.targeting) : value }))}
          placeholder="Targeting"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.categoryOptions}
          selected={filters.category}
          setSelected={(value) => setFilters(prev => ({ ...prev, category: typeof value === 'function' ? value(prev.category) : value }))}
          placeholder="Category"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.publisherOptions}
          selected={filters.publisher}
          setSelected={(value) => setFilters(prev => ({ ...prev, publisher: typeof value === 'function' ? value(prev.publisher) : value }))}
          placeholder="Publisher"
          className="min-w-[140px]"
        />

        {/* Export the whole campaign (flights → assets → versions → media) to Excel */}
        <Button
          type="secondary"
          className="ml-auto whitespace-nowrap flex items-center gap-1.5"
          onClick={handleExportAssets}
          disabled={isExporting}
        >
          <Download size={16} />
          {isExporting ? "Exporting..." : "Export Assets (Excel)"}
        </Button>

        {/* Move all draft assets in this campaign to briefed */}
        <Button
          type="primary"
          className="bg-[#EA33C0] text-white hover:bg-[#d629a8] whitespace-nowrap"
          onClick={handleBriefAssets}
          disabled={isBriefing}
        >
          {isBriefing ? "Moving..." : "Move Drafts to Briefed"}
        </Button>
      </div>

      {/* Flight Sections */}
      {campaignDetail.nested_by_flight.map((flight, flightIndex) => {
        const flightKey = `flight-${flightIndex}`;
        const isExpanded = expandedFlights[flightKey];
        const assets = getFlightAssets(flight.flight_id);
        const filteredAssets = filterAssets(assets);
        const totalAssets = filteredAssets.length;

        return (
          <div key={flight.flight_id} className="rounded-xl">
            {/* Flight Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Flight {flight.flight_number}
              </h2>
              <div className="flex items-center gap-4">
                <span className="text-lg font-semibold text-gray-700">
                  {totalAssets}
                </span>
                <DeleteConfirmationControl
                  itemLabel={`Flight ${flight.flight_number}`}
                  ariaLabel={`Delete Flight ${flight.flight_number}`}
                  title={`Delete Flight ${flight.flight_number}?`}
                  description={`This removes Flight ${flight.flight_number}, all of its assets, versions, and stored media from the campaign. The records and media are retained for 30 days, and an export of everything deleted is emailed to you.`}
                  onConfirm={() => handleDeleteFlight(flight.flight_id)}
                  onError={(message) => setError(message)}
                />
                <button
                  onClick={() => toggleFlight(flightKey)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronUp
                    size={20}
                    className={`transition-transform duration-200 ${
                      isExpanded ? "rotate-0" : "rotate-180"
                    }`}
                  />
                </button>
              </div>
            </div>

            <hr className="mt-2 border-1 border-gray-300 mb-2" />

            {/* Table */}
            {isExpanded && (
              <div>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 pb-3 mb-2 text-sm font-semibold text-gray-700 border-b border-gray-300">
                  <div className="col-span-1"></div>
                  <div className="col-span-1 flex items-center gap-1">
                    Language
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Objective
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Region
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Category
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Publisher
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Ad Type
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Due Date
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Status
                  </div>
                </div>

                {/* Table Body */}
                {filteredAssets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No assets found for the selected filters
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="grid grid-cols-12 gap-4 py-3 text-sm items-center hover:bg-gray-50 rounded transition-colors"
                      >
                        <div className="col-span-1">
                          <img
                            src={asset.thumbnail}
                            alt="Asset thumbnail"
                            className="w-10 h-10 rounded object-cover"
                          />
                        </div>
                        <div className="col-span-1 font-medium">{asset.language}</div>
                        <div className="col-span-1">{asset.objective}</div>
                        <div className="col-span-1">{asset.region}</div>
                        <div className="col-span-1">{asset.category}</div>
                        <div className="col-span-2">{asset.publisher}</div>
                        <div className="col-span-2">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/campaign/assets?id=${campaignDetail.metadata.campaign.id}`,
                              )
                            }
                            className="text-[#EA33C0] hover:underline font-medium text-left"
                          >
                            {asset.adType}
                          </button>
                        </div>
                        <div className="col-span-1">{formatDate(asset.dueDate)}</div>
                        {/* Upstream's layout wrapper, which keeps the delete
                            control pinned to the right of the status circles. */}
                        <div className="col-span-2 flex items-center justify-between gap-2">
                          {/* Each status is a colour-filled circle holding its
                              count. The colours are taken from the status icons
                              this replaced, so the mapping stays familiar. */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {([
                              { color: STATUS_COLORS.draft,        label: "Draft",        count: asset.statusCounts.draft },
                              { color: STATUS_COLORS.briefed,      label: "Briefed",      count: asset.statusCounts.briefed },
                              { color: STATUS_COLORS.in_progress,  label: "In Progress",  count: asset.statusCounts.in_progress },
                              { color: STATUS_COLORS.in_review,    label: "In Review",    count: asset.statusCounts.in_review },
                              { color: STATUS_COLORS.approved,     label: "Approved",     count: asset.statusCounts.approved },
                              { color: STATUS_COLORS.trafficking,  label: "Trafficking",  count: asset.statusCounts.trafficking },
                              { color: STATUS_COLORS.live,         label: "Live",         count: asset.statusCounts.live },
                            ] as const)
                              .map(({ color, label, count }) => (
                                <div
                                  key={label}
                                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    count > 0 ? "" : "opacity-30"
                                  }`}
                                  style={{ backgroundColor: color }}
                                  title={`${label}: ${count}`}
                                >
                                  <span className="text-xs font-semibold text-gray-800">
                                    {count}
                                  </span>
                                </div>
                              ))}
                          </div>
                          <DeleteConfirmationControl
                            itemLabel={`${asset.publisher} ${asset.adType} asset`}
                            ariaLabel="Delete asset"
                            title="Delete asset?"
                            description="This removes the asset, its versions, and its stored media from the campaign. The records and media are retained for 30 days, and an export of everything deleted is emailed to you."
                            onConfirm={() => handleDeleteAsset(asset.assetId)}
                            onError={(message) => setError(message)}
                            iconSize={16}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
